import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Star, Trash2, Eye, Plus, FileText, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import api from "../lib/api";

export default function MailTemplates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("letter");
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [htmlMap, setHtmlMap] = useState({});
  const [form, setForm] = useState({ name: "", image: null, front_image: null, back_image: null });
  const refs = { image: useRef(), front: useRef(), back: useRef() };

  const load = () => {
    setLoading(true);
    api.get("/mailer-templates").then((r) => {
      const its = r.data.items || [];
      setItems(its);
      its.filter((t) => t.source === "builtin").forEach((t) => {
        api.get(`/postgrid/template-preview?template_id=${t.id}&type=${t.type}`)
          .then((res) => setHtmlMap((m) => ({ ...m, [t.id]: res.data })))
          .catch(() => {});
      });
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { document.title = "ARQITEK — Mail Templates"; load(); }, []);

  const list = items.filter((t) => t.type === tab);

  const upload = async (key, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/mailer-templates/upload", fd);
      setForm((p) => ({ ...p, [key]: data.data_uri }));
    } catch (e) { toast.error("Upload failed"); } finally { setBusy(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Give the design a name");
    if (tab === "letter" && !form.image) return toast.error("Upload the letter design");
    if (tab === "postcard" && !form.front_image) return toast.error("Upload the postcard front");
    setBusy(true);
    try {
      await api.post("/mailer-templates", {
        name: form.name, type: tab, image: form.image,
        front_image: form.front_image, back_image: form.back_image, make_default: false,
      });
      toast.success("Design saved");
      setCreating(false); setForm({ name: "", image: null, front_image: null, back_image: null }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };

  const setDefault = async (t) => { await api.post(`/mailer-templates/${t.id}/default`); toast.success(`"${t.name}" is now the default ${t.type}`); load(); };
  const del = async (t) => { if (!window.confirm(`Delete "${t.name}"?`)) return; await api.delete(`/mailer-templates/${t.id}`); toast.success("Deleted"); load(); };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="mail-templates">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="block text-xs uppercase tracking-[0.15em] text-primary mb-2">Operations</span>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Mail Templates</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Upload your own letter and postcard designs, preview them, and set which one to mail. Pick a design per type on the Send a Mailer screen.</p>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="mt-new-btn"><Plus className="h-4 w-4 mr-2" />Upload design</Button>
      </div>

      <div className="flex gap-2" data-testid="mt-tabs">
        {["letter", "postcard"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`mt-tab-${t}`}
            className={`px-4 py-2 rounded-lg text-sm capitalize border transition-colors ${tab === t ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}>{t}s</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="mt-grid">
          {list.map((t) => {
            const img = t.image || t.front_image;
            const html = htmlMap[t.id];
            const isPostcard = t.type === "postcard";
            const thumbHtml = html ? (isPostcard ? html.front_html : html.letter_html) : null;
            return (
              <div key={t.id} className="rounded-xl border border-border bg-card overflow-hidden flex flex-col" data-testid="mt-card">
                <div className="relative bg-white aspect-[4/3] flex items-center justify-center overflow-hidden">
                  {img ? (
                    <img src={img} alt={t.name} className="w-full h-full object-cover" />
                  ) : thumbHtml ? (
                    <div className="absolute inset-0 overflow-hidden bg-white">
                      <ScaledHtml html={thumbHtml} natW={isPostcard ? 900 : 850} natH={isPostcard ? 600 : 1100} className="w-full" />
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground p-4"><FileText className="h-8 w-8 mx-auto mb-2" /><div className="text-xs">Personalized per lead</div></div>
                  )}
                  {t.is_default && <span className="absolute top-2 left-2 z-10 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary text-white flex items-center gap-1"><Star className="h-3 w-3" />Default</span>}
                  {t.source === "builtin" && <span className="absolute top-2 right-2 z-10 text-[10px] px-2 py-0.5 rounded-full bg-black/60 text-white">Built-in</span>}
                </div>
                <div className="p-3 flex-1 flex flex-col">
                  <div className="font-medium text-sm">{t.name}</div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {(img || html) && <Button variant="outline" size="sm" onClick={() => setPreview(t)} data-testid="mt-preview-btn"><Eye className="h-3.5 w-3.5 mr-1.5" />Preview</Button>}
                    {!t.is_default && <Button variant="outline" size="sm" onClick={() => setDefault(t)} data-testid="mt-default-btn"><Star className="h-3.5 w-3.5 mr-1.5" />Set default</Button>}
                    {t.source !== "builtin" && <Button variant="ghost" size="sm" onClick={() => del(t)} data-testid="mt-delete-btn"><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="mt-upload-dialog">
          <DialogHeader><DialogTitle>Upload a {tab} design</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input placeholder="Design name (e.g. Spring Campaign)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="mt-name-input" />
            {tab === "letter" ? (
              <UploadBox label="Letter design (full page)" uri={form.image} inputRef={refs.image} onPick={(f) => upload("image", f)} onClear={() => setForm({ ...form, image: null })} testid="mt-letter-upload" />
            ) : (
              <>
                <UploadBox label="Postcard front *" uri={form.front_image} inputRef={refs.front} onPick={(f) => upload("front_image", f)} onClear={() => setForm({ ...form, front_image: null })} testid="mt-front-upload" />
                <UploadBox label="Postcard back (optional — leave blank to keep the smart back with seller QR)" uri={form.back_image} inputRef={refs.back} onPick={(f) => upload("back_image", f)} onClear={() => setForm({ ...form, back_image: null })} testid="mt-back-upload" />
              </>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
              <Button onClick={save} disabled={busy} data-testid="mt-save-btn">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Save design</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-2xl" data-testid="mt-preview-dialog">
          <DialogHeader><DialogTitle>{preview?.name}</DialogTitle></DialogHeader>
          {preview && (() => {
            const html = htmlMap[preview.id];
            if (preview.source === "builtin" && html) {
              if (preview.type === "postcard") {
                return (
                  <div className="space-y-3 max-h-[72vh] overflow-y-auto">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Front</div>
                    <ScaledHtml html={html.front_html} natW={900} natH={600} className="w-full rounded-md border border-border" />
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Back <span className="normal-case tracking-normal">— personalized per lead (name, address, APN + seller QR)</span></div>
                    <ScaledHtml html={html.back_html} natW={900} natH={600} className="w-full rounded-md border border-border" />
                  </div>
                );
              }
              return (
                <div className="max-h-[72vh] overflow-y-auto">
                  <ScaledHtml html={html.letter_html} natW={850} natH={1100} className="w-full rounded-md border border-border" />
                </div>
              );
            }
            if (preview.type === "postcard") {
              return (
                <div className="space-y-3 max-h-[72vh] overflow-y-auto">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Front</div>
                  <img src={preview.front_image} alt="front" className="w-full rounded-md border border-border" />
                  {preview.back_image ? (<><div className="text-xs uppercase tracking-wider text-muted-foreground">Back</div><img src={preview.back_image} alt="back" className="w-full rounded-md border border-border" /></>) : <div className="text-xs text-muted-foreground">Back uses the smart Arqitek layout (lead details + seller QR).</div>}
                </div>
              );
            }
            return <img src={preview?.image} alt="letter" className="w-full rounded-md border border-border" />;
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Renders full-page mail HTML (built-in dynamic designs) scaled to fit the
// container width — used for card thumbnails and the preview modal.
function ScaledHtml({ html, natW, natH, className = "" }) {
  const ref = useRef();
  const [scale, setScale] = useState(null);
  useEffect(() => {
    if (!ref.current) return;
    const measure = () => { if (ref.current) setScale(ref.current.clientWidth / natW); };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [natW]);
  return (
    <div ref={ref} className={className} style={{ height: scale ? natH * scale : Math.round(natH * 0.3), overflow: "hidden", background: "#fff" }}>
      {scale != null && (
        <iframe title="preview" srcDoc={html} scrolling="no"
          style={{ width: natW, height: natH, transform: `scale(${scale})`, transformOrigin: "top left", border: 0, pointerEvents: "none", background: "#fff" }} />
      )}
    </div>
  );
}

function UploadBox({ label, uri, inputRef, onPick, onClear, testid }) {
  return (
    <div>
      <div className="text-sm text-muted-foreground mb-1.5">{label}</div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} data-testid={testid} />
      {uri ? (
        <div className="relative inline-block">
          <img src={uri} alt="preview" className="h-28 rounded-md border border-border" />
          <button onClick={onClear} className="absolute -top-2 -right-2 bg-background border border-border rounded-full p-1"><X className="h-3 w-3" /></button>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()}><Upload className="h-4 w-4 mr-1.5" />Choose image</Button>
      )}
    </div>
  );
}
